import React, { useState, useEffect } from "react"
import { Modal, Button, Flex, Select } from "antd"
import { Trans, useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import styles from "../roleModals.module.css"

const DeleteRoleWithReassignModal = ({ open, onCancel, onConfirm, roleName, assignedStaff = [], availableRoles = [] }) => {
  const { t } = useTranslation()
  const [selectedNewRole, setSelectedNewRole] = useState(null)

  const hasAssignedStaff = assignedStaff.length > 0
  const staffCount = assignedStaff.length

  useEffect(() => {
    if (!open) {
      setSelectedNewRole(null)
    }
  }, [open])

  const handleConfirm = () => {
    onConfirm(hasAssignedStaff ? selectedNewRole : null)
  }

  const canDelete = !hasAssignedStaff || selectedNewRole

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={hasAssignedStaff ? 400 : 370}
      className={styles.roleModal}
      closeIcon={
        <span className={styles.closeIcon}>
          <Icon type="feather" name="X" />
        </span>
      }
    >
      <div className={styles.modalHeader}>
        <h2 className={styles.modalTitle}>{t("staff.role_modal.delete_title")}</h2>
      </div>
      <div className={styles.modalBody}>
        {hasAssignedStaff ? (
          <>
            <Text className={styles.deleteDescription}>
              <Trans
                i18nKey={staffCount > 1 ? "staff.role_modal.delete_assigned_message_plural" : "staff.role_modal.delete_assigned_message"}
                values={{ roleName, count: staffCount }}
                components={{ strong: <strong /> }}
              />
              <br />
              {t("staff.role_modal.delete_select_new_role")}
            </Text>
            <div className={styles.staffListSection}>
              <Text className={styles.staffListTitle}>
                {roleName ? t("staff.role_modal.staff_list_title", { roleName }) : t("staff.role_modal.staff_list_title_default")}
              </Text>
              <div className={styles.staffList}>
                {assignedStaff.map((staff, index) => (
                  <div key={staff.id} className={styles.staffListItem}>
                    <Text className={styles.staffItemName}>
                      {index + 1}. {staff.name}
                    </Text>
                    {staff.phone && (
                      <Flex gap={8} align="center" className={styles.staffItemContact}>
                        <Icon type="feather" name="Phone" width={14} height={14} />
                        <Text className={styles.staffItemContactText}>{staff.phone}</Text>
                      </Flex>
                    )}
                    {staff.email && (
                      <Flex gap={8} align="center" className={styles.staffItemContact}>
                        <Icon type="feather" name="Mail" width={14} height={14} />
                        <Text className={styles.staffItemContactText}>{staff.email}</Text>
                      </Flex>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.roleReassignSection}>
              <Flex gap={16} align="center">
                <div className={styles.currentRoleBox}>
                  <Text className={styles.roleLabel}>{t("staff.role_modal.current_role")}</Text>
                  <div className={styles.roleValue}>{roleName}</div>
                </div>
                <Icon type="feather" name="ArrowRight" width={20} height={20} className={styles.arrowIcon} />
                <div className={styles.newRoleBox}>
                  <Text className={styles.roleLabel}>{t("staff.role_modal.new_role")}</Text>
                  <Select
                    placeholder={t("staff.role_modal.select_a_role")}
                    style={{ width: "100%" }}
                    value={selectedNewRole}
                    onChange={setSelectedNewRole}
                    options={availableRoles.map((role) => ({
                      label: role.name,
                      value: role.id
                    }))}
                  />
                </div>
              </Flex>
            </div>
          </>
        ) : (
          <Text className={styles.deleteDescription}>
            <Trans i18nKey="staff.role_modal.delete_permanent_message" values={{ roleName }} components={{ strong: <strong /> }} />
            <br />
            {t("staff.role_modal.delete_cant_undo")}
          </Text>
        )}
      </div>
      <Flex gap={12} className={styles.modalFooter}>
        <Button size="large" onClick={onCancel}>
          {t("staff.role_modal.cancel")}
        </Button>
        <Button type="primary" danger size="large" onClick={handleConfirm} disabled={!canDelete}>
          {t("staff.role_modal.delete")}
        </Button>
      </Flex>
    </Modal>
  )
}

export default DeleteRoleWithReassignModal
